import React from 'react';
import { CircleAlertIcon, FileSearchIcon, LightbulbIcon } from 'lucide-react';
import type { PreviewStatus, Tool } from '../../types/tools';
import { ToolIcon } from './ToolIcon';
import { cn } from '../../src/lib/utils';
const STATUS: Record<PreviewStatus, {
  label: string;
  className: string;
}> = {
  strong: {
    label: 'Supported',
    className: 'border-[#E3DFF4] bg-[#F5F3FC] text-lime'
  },
  partial: {
    label: 'Partial',
    className: 'border-[#F0E0C4] bg-[#FDF7ED] text-[#92400E]'
  },
  gap: {
    label: 'Missing',
    className: 'border-[#F3D4D4] bg-[#FDF3F3] text-[#9F1D1D]'
  },
  neutral: {
    label: 'Review',
    className: 'border-hairline bg-elevated text-text-secondary'
  }
};
type ToolOutputPreviewProps = {
  tool: Tool;
  /** Compact = hero / thumbnail; full = illustrative output section. */
  variant?: 'compact' | 'full';
  className?: string;
};

/** Readable sample deliverable. Always labelled as an illustrative example. */
export function ToolOutputPreview({
  tool,
  variant = 'full',
  className
}: ToolOutputPreviewProps) {
  const {
    preview
  } = tool;
  const compact = variant === 'compact';
  const rows = compact ? preview.rows?.slice(0, 4) : preview.rows;
  const sections = compact ? preview.sections?.slice(0, 3) : preview.sections;
  return <figure className={cn('overflow-hidden rounded-2xl border border-hairline bg-panel shadow-[0_24px_60px_-40px_rgba(32,22,95,0.35)]', className)} aria-label={`Illustrative example of ${tool.name} output: ${preview.kind}`} data-id="element-1197" data-mp="2h3gtrbrww078">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline-soft bg-surface px-4 py-3 sm:px-5" data-id="element-1198" data-mp="227hlo14sy023">
        <div className="flex min-w-0 items-center gap-3" data-id="element-1199" data-mp="1y6fmtlesrq0n">
          <ToolIcon name={tool.icon} size="sm" data-id="element-1200" data-mp="1t7ukngfziw9k" />
          <div className="min-w-0" data-id="element-1201" data-mp="1t95othdce617">
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-text-tertiary" data-id="element-1202" data-mp="3sjx4irsitsnr">{preview.kind}</p>
            <p className="truncate text-sm font-semibold text-text-primary" data-id="element-1203" data-mp="3nku8fwuiqwrx">{preview.title}</p>
          </div>
        </div>
        <span className="rounded-md border border-[#F0E0C4] bg-[#FDF7ED] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#92400E]" data-id="element-1204" data-mp="ajje5fwyxki3">
          Illustrative example
        </span>
      </div>

      <div className="flex flex-col gap-4 p-4 sm:p-5" data-id="element-1205" data-mp="1lhfv84md5yi">
        <p className="text-xs text-text-secondary" data-id="element-1206" data-mp="6hy2tz031ea0">{preview.subject}</p>

        {preview.metrics ? <dl className={cn('grid gap-2', preview.metrics.length > 2 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2')} data-id="element-1207" data-mp="u82788l3hi22">
            {preview.metrics.map(metric => <div key={metric.label} className="rounded-xl border border-hairline-soft bg-surface px-3 py-2.5" data-id="element-1208" data-mp="ka4hthri1f7v">
                <dt className="text-[10px] uppercase tracking-[0.08em] text-text-tertiary" data-id="element-1209" data-mp="p8pkdnu84azh">{metric.label}</dt>
                <dd className="mt-1 font-mono text-base font-semibold text-text-primary" data-id="element-1210" data-mp="3503ntlozhe7u">{metric.value}</dd>
                {metric.note && !compact ? <dd className="mt-0.5 text-[11px] text-text-tertiary" data-id="element-1211" data-mp="3a113nrmitw66">{metric.note}</dd> : null}
              </div>)}
          </dl> : null}

        {rows ? <ul className="flex flex-col divide-y divide-hairline-soft rounded-xl border border-hairline-soft" data-id="element-1212" data-mp="2xfst8v5qzcnc">
            {rows.map(row => <li key={row.label} className="flex flex-col gap-1.5 px-3 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4" data-id="element-1213" data-mp="3be96z62123ez">
                <div className="min-w-0" data-id="element-1214" data-mp="2ir6disc7kivl">
                  <p className="text-[13px] font-medium text-text-primary" data-id="element-1215" data-mp="2is7nsuleh1c9">{row.label}</p>
                  <p className="text-xs leading-relaxed text-text-secondary" data-id="element-1216" data-mp="2erxg9dhym2va">{row.detail}</p>
                  {row.source && !compact ? <p className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] text-text-tertiary" data-id="element-1217" data-mp="2nrauskkxxhmv">
                      <FileSearchIcon className="h-3 w-3" aria-hidden="true" data-id="element-1218" data-mp="200ybyeo5q0hi" />
                      Source: {row.source}
                    </p> : null}
                </div>
                <span className={cn('w-fit shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em]', STATUS[row.status].className)} data-id="element-1219" data-mp="28pcazn50lro9">
                  {STATUS[row.status].label}
                </span>
              </li>)}
          </ul> : null}

        {preview.table ? <div className="overflow-x-auto rounded-xl border border-hairline-soft" data-id="element-1220" data-mp="2rek4v0b67b1x">
            <table className="w-full min-w-[420px] text-left text-xs" data-id="element-1221" data-mp="2wdvn6dr6qolm">
              <caption className="sr-only" data-id="element-1222" data-mp="31drq09o5c9zl">{preview.table.caption}</caption>
              <thead className="bg-surface text-[10px] uppercase tracking-[0.08em] text-text-tertiary" data-id="element-1223" data-mp="31boq8xx6z2ho">
                <tr data-id="element-1224" data-mp="36aruig5cl4ol">
                  {preview.table.columns.map(column => <th key={column} scope="col" className="px-3 py-2 font-medium" data-id="element-1225" data-mp="171l4agxmmat1">
                      {column}
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline-soft" data-id="element-1226" data-mp="3paeb3u38qdo2">
                {preview.table.rows.map(row => <tr key={row[0]} data-id="element-1227" data-mp="3kgjtcw7748rm">
                    {row.map((cell, index) => <td key={`${row[0]}-${index}`} className={cn('px-3 py-2', index === 0 ? 'font-medium text-text-primary' : 'text-text-secondary')} data-id="element-1228" data-mp="3geosttx39zew" data-mpi={index}>
                        {cell}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div> : null}

        {sections ? <div className="flex flex-col gap-3" data-id="element-1229" data-mp="3u8pily62cc34">
            {sections.map(section => <div key={section.heading} className="rounded-xl border border-hairline-soft px-3 py-2.5" data-id="element-1230" data-mp="2nrud1ab7vo7r">
                <p className="text-[13px] font-semibold text-text-primary" data-id="element-1231" data-mp="2ishq6pkcih56">{section.heading}</p>
                <p className="mt-1 text-xs leading-relaxed text-text-secondary" data-id="element-1232" data-mp="2kag3si8wnwuw">{section.body}</p>
                {section.source && !compact ? <p className="mt-1.5 inline-flex items-center gap-1 font-mono text-[10px] text-text-tertiary" data-id="element-1233" data-mp="2p8re0u3449dn">
                    <FileSearchIcon className="h-3 w-3" aria-hidden="true" data-id="element-1234" data-mp="2bbm9wzmylkl1" />
                    Source: {section.source}
                  </p> : null}
              </div>)}
          </div> : null}

        {!compact && preview.assumptions ? <div className="rounded-xl border border-hairline-soft bg-surface px-3 py-2.5" data-id="element-1235" data-mp="26bzvwnzb40b6">
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-secondary" data-id="element-1236" data-mp="1skbimhdbn5sm">
              <LightbulbIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1237" data-mp="1wi99domqpzy4" />
              Assumptions
            </p>
            <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs text-text-secondary" data-id="element-1238" data-mp="1njxjxpxm1879">
              {preview.assumptions.map(item => <li key={item} data-id="element-1239" data-mp="13yx2eq2wte7k">{item}</li>)}
            </ul>
          </div> : null}

        <div className="rounded-xl border border-[#F0E0C4] bg-[#FDF7ED] px-3 py-2.5" data-id="element-1240" data-mp="z0carf1q42uq">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#92400E]" data-id="element-1241" data-mp="1dxcsqqu44jg0">
            <CircleAlertIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1242" data-mp="18vxa15feqp0s" />
            Information gaps
          </p>
          <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs text-[#7A3A0C]" data-id="element-1243" data-mp="1mun8aodyquqq">
            {(compact ? preview.gaps.slice(0, 2) : preview.gaps).map(gap => <li key={gap} data-id="element-1244" data-mp="1wm9qj4opxu2z">{gap}</li>)}
          </ul>
        </div>
      </div>

      <figcaption className="border-t border-hairline-soft px-4 py-2.5 text-[11px] text-text-tertiary sm:px-5" data-id="element-1245" data-mp="1rp1770ru3k05">
        Illustrative example with fictional company data and sample scores. Not a customer result or an investment
        recommendation.
      </figcaption>
    </figure>;
}